// ---------------------------------------------------------------------------
// A ESCOLHA DA UNIDADE POR PROXIMIDADE.
//
// A conta roda NO NAVEGADOR do cliente, sobre as coordenadas que o SSR já
// embutiu no HTML. A localização do cliente NUNCA vai para o servidor: não
// precisamos dela, e guardar onde o cliente estava quando pediu uma pizza é um
// dado que não queremos ter.
//
// A validação de faixa roda no SERVIDOR, na gravação. Coordenada trocada (lat e
// lng invertidos é o erro clássico) manda o cliente para a unidade errada, e o
// erro é silencioso — não existe tela em que ele apareça.
// ---------------------------------------------------------------------------

export interface Coordenada {
  lat: number;
  lng: number;
}

/**
 * A caixa que contém o Brasil inteiro, com folga.
 *
 * Não é elegante e é de propósito: pega o erro que acontece de verdade (lat e
 * lng invertidos, zero/zero, grau decimal com vírgula virando outro número) sem
 * precisar de uma base de fronteiras.
 */
export const LIMITE_BRASIL = { latMin: -34, latMax: 6, lngMin: -74, lngMax: -34 };

export function coordenadaValida(coordenada: Partial<Coordenada>): boolean {
  const { lat, lng } = coordenada;
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return (
    lat >= LIMITE_BRASIL.latMin &&
    lat <= LIMITE_BRASIL.latMax &&
    lng >= LIMITE_BRASIL.lngMin &&
    lng <= LIMITE_BRASIL.lngMax
  );
}

const RAIO_DA_TERRA_METROS = 6_371_000;

/** Distância em metros entre dois pontos. Haversine — erro < 0,5% na escala de cidade. */
export function distanciaEmMetros(a: Coordenada, b: Coordenada): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * RAIO_DA_TERRA_METROS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** "1,2 km" / "480 m". Abaixo de 1 km em metros redondos: é o que o pedestre lê. */
export function formatarDistancia(metros: number): string {
  if (!Number.isFinite(metros) || metros < 0) return "";
  if (metros < 1000) return `${Math.round(metros / 10) * 10} m`;
  const km = metros / 1000;
  return `${km.toFixed(km < 10 ? 1 : 0).replace(".", ",")} km`;
}

/** Pino para conferência humana. É por aqui que a coordenada é aprovada. */
export function linkDoMapa(coordenada: Coordenada): string {
  return `https://www.google.com/maps?q=${coordenada.lat},${coordenada.lng}`;
}

/** Traçar rota até a unidade — o que o botão "como chegar" abre. */
export function linkDeRota(coordenada: Coordenada): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${coordenada.lat},${coordenada.lng}`;
}

/**
 * Ordena unidades pela distância a um ponto.
 *
 * Quem NÃO tem coordenada não fica de fora — fica DEPOIS, na ordem original.
 * Sumir seria pior: uma unidade sem coordenada conferida ainda é uma loja que
 * existe e atende telefone.
 */
export function ordenarPorProximidade<T extends { lat: number | null; lng: number | null }>(
  unidades: T[],
  origem: Coordenada,
): (T & { distanciaEmMetros: number | null })[] {
  return unidades
    .map((unidade) => ({
      ...unidade,
      distanciaEmMetros:
        unidade.lat !== null && unidade.lng !== null
          ? distanciaEmMetros(origem, { lat: unidade.lat, lng: unidade.lng })
          : null,
    }))
    .sort((a, b) => {
      if (a.distanciaEmMetros === null && b.distanciaEmMetros === null) return 0;
      if (a.distanciaEmMetros === null) return 1;
      if (b.distanciaEmMetros === null) return -1;
      return a.distanciaEmMetros - b.distanciaEmMetros;
    });
}
